import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Container,
  Header,
  Box,
  Button,
  Alert,
  SpaceBetween,
  ProgressBar,
  Icon
} from '@cloudscape-design/components';
import { imageLinkApi } from './imageLinkApi.js';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff', 'image/svg+xml'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.svg'];

function isImageFile(file) {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return ACCEPTED_EXTENSIONS.includes(ext);
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function UploadSection({ onUploadComplete }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [username, setUsername] = useState('unknown');
  const [uploadPhase, setUploadPhase] = useState(''); // uploading, saving, done
  const fileInputRef = useRef(null);

  // Harmony username detection
  useEffect(() => {
    const waitForHarmonyUser = async () => {
      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      let retries = 5;
      let login = 'unknown';
      while (retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) { login = user; break; }
        await wait(300);
      }
      setUsername(login);
    };
    waitForHarmonyUser();
  }, []);

  // Generate thumbnail previews (cap at 12 to avoid browser freeze)
  const MAX_PREVIEWS = 12;
  const previews = useMemo(() => {
    return selectedFiles.slice(0, MAX_PREVIEWS).map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
  }, [selectedFiles]);

  // Cleanup object URLs
  useEffect(() => {
    return () => previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  const handleFiles = (fileList) => {
    const images = Array.from(fileList).filter(isImageFile);
    const skipped = fileList.length - images.length;
    if (images.length === 0) {
      setError('No valid image files selected. Accepted: JPG, PNG, GIF, WebP, BMP, TIFF, SVG');
      return;
    }
    if (skipped > 0) {
      setError(`${skipped} file${skipped > 1 ? 's' : ''} skipped (unsupported format)`);
    } else {
      setError('');
    }
    setSuccess('');
    setSelectedFiles(images);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleZoneClick = () => { fileInputRef.current?.click(); };
  const handleFileInputChange = (e) => { if (e.target.files.length > 0) handleFiles(e.target.files); };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadPhase('uploading');
    setProgress({ completed: 0, total: selectedFiles.length });

    try {
      const runId = imageLinkApi.createRunId(username);
      const results = await imageLinkApi.uploadFiles(selectedFiles, runId, (p) => {
        setProgress({ completed: p.completed, total: p.total });
      });

      setUploadPhase('saving');
      const { metadata } = await imageLinkApi.saveResults(runId, username, results);

      setUploadPhase('done');
      const okCount = metadata.success_count || 0;
      const failCount = metadata.failed_count || 0;
      let msg = `${okCount} image${okCount !== 1 ? 's' : ''} uploaded successfully 🎉`;
      if (failCount > 0) msg += ` (${failCount} failed)`;
      setSuccess(msg);
      setSelectedFiles([]);
      if (onUploadComplete) onUploadComplete(metadata);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadPhase('');
    }
  };

  const dropZoneClasses = [
    'image-link-drop-zone',
    dragOver ? 'drag-over' : '',
    uploading ? 'disabled' : ''
  ].filter(Boolean).join(' ');

  const progressLabel = uploadPhase === 'saving' ? 'Saving results...'
    : uploadPhase === 'done' ? 'Done ✅'
    : `Uploading ${progress.completed} of ${progress.total} files...`;

  return (
    <Container
      header={
        <Header variant="h2" description="Drop or select images to generate permanent URLs">
          🖼️ Upload Images
        </Header>
      }
    >
      <SpaceBetween size="m">
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

        <div>
          <div
            role="button" tabIndex={0} className={dropZoneClasses}
            onClick={handleZoneClick} onDrop={handleDrop}
            onDragOver={handleDragOver} onDragLeave={handleDragLeave}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleZoneClick(); }}
            aria-label="Drop images here or click to browse"
          >
            <p className="drop-zone-icon">📁</p>
            <p className="drop-zone-title">Drag & drop images here, or click to select</p>
            <p className="drop-zone-hint">JPG · PNG · GIF · WebP · BMP · TIFF · SVG</p>
          </div>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')}
            multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
        </div>

        {/* File preview grid */}
        {selectedFiles.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
              {selectedFiles.length} image{selectedFiles.length !== 1 ? 's' : ''} selected
            </Box>
            <div className="file-preview-grid">
              {previews.map((p, i) => (
                <div key={i} className="file-preview-item">
                  <img src={p.url} alt={p.file.name} className="file-preview-thumb" />
                  <div className="file-preview-info">
                    <span className="file-preview-name" title={p.file.name}>
                      {p.file.name.length > 20 ? p.file.name.slice(0, 18) + '...' : p.file.name}
                    </span>
                    <span className="file-preview-size">{formatSize(p.file.size)}</span>
                  </div>
                  <button className="file-preview-remove" onClick={() => removeFile(i)}
                    aria-label={`Remove ${p.file.name}`}>✕</button>
                </div>
              ))}
              {selectedFiles.length > MAX_PREVIEWS && (
                <div className="file-preview-more">
                  +{selectedFiles.length - MAX_PREVIEWS} more
                </div>
              )}
            </div>
          </div>
        )}

        {uploading && (
          <ProgressBar
            value={progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}
            additionalInfo={progressLabel}
            label="Upload progress"
            status={uploadPhase === 'done' ? 'success' : 'in-progress'}
          />
        )}

        {selectedFiles.length > 0 && (
          <Button variant="primary" onClick={handleUpload} loading={uploading}
            disabled={uploading}>
            🚀 Upload {selectedFiles.length} Image{selectedFiles.length !== 1 ? 's' : ''}
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}
